/**
 * useChannel Hook
 *
 * Central hook for multi-tenant channel configuration that determines the entire app's
 * branding, theming, content access, and business logic based on subdomain.
 *
 * Key architectural decisions:
 * - Uses subdomain-based channel resolution (e.g., bristolcity.localhost:5173)
 * - Single source of truth for tenant-specific configuration across entire app
 * - React Query for caching to prevent redundant API calls for static config
 *
 * Business rules:
 * - Channel configuration drives ALL visual customization (colors, logos, fonts)
 * - Channel determines available content, pricing tiers, and features
 * - Channel settings control third-party integrations (Intercom, analytics)
 *
 * WHY React Query instead of Context:
 * - Channel config rarely changes during session (stale-while-revalidate ideal)
 * - Automatic retry on network failure ensures config always loads
 * - Shared cache across all components prevents duplicate requests
 *
 * @returns {Object} channel - Complete channel configuration including:
 *   - theme_color: Brand colors for UI customization
 *   - meta: Third-party integration IDs (chatbot, analytics)
 *   - subscription_plans: Available pricing tiers
 *   - content_access: Which fixtures/collections are available
 *
 * @see src/utils/constants.js - For API endpoint configuration
 * @see src/features/authentication/CLAUDE.md - Channel impacts auth flows
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getChannel, getChannelId } from '../services/apiChannel';
import type { ChannelResponse } from '../types/api';

export interface UseChannelResult {
  isLoading: boolean;
  channel: ChannelResponse | undefined;
  error: Error | null;
}

export function useChannel(): UseChannelResult {
  const {
    isLoading,
    data: channel,
    error,
  }: UseQueryResult<ChannelResponse, Error> = useQuery({
    queryKey: ['club'],
    queryFn: () => getChannel(),
    // WHY commented: Toast notifications moved to error boundaries for centralized error handling
    // Prevents duplicate toasts when multiple components use this hook
    // onError: (err) => toast.error(err.message),

    // WHY retry: 1 instead of default 3:
    // Channel config is critical for app initialization - if it fails twice,
    // the subdomain likely doesn't exist or API is down. Faster failure = better UX
    // than waiting through 3 retries for a 404 response.
    retry: 1,
  });

  return { isLoading, channel, error: error ?? null };
}

/**
 * useChannelId Hook
 *
 * Lightweight hook that retrieves only the channel ID without full configuration.
 * Used for API calls that need tenant identification without loading entire config.
 *
 * WHY separate from useChannel:
 * - Performance optimization: Some operations only need the ID (e.g., analytics tracking)
 * - Reduces payload size when full config isn't needed (ID response ~50 bytes vs full config ~5KB)
 * - Prevents unnecessary re-renders in components that don't need theme/branding data
 *
 * Business use cases:
 * - Analytics event tracking needs channel ID but not theme colors
 * - API authentication headers require tenant ID
 * - Logging and monitoring systems need channel context
 *
 * @returns {Object} channelId - Unique identifier for the current tenant/channel
 */
export interface UseChannelIdResult {
  isLoading: boolean;
  channelId: string | undefined;
  error: Error | null;
}

export function useChannelId(): UseChannelIdResult {
  const {
    isLoading,
    data: channelId,
    error,
  }: UseQueryResult<string, Error> = useQuery({
    // WHY different queryKey from useChannel:
    // Separate cache entry allows both hooks to coexist without conflicts
    // Components can fetch just ID or full config based on needs
    queryKey: ['channelId'],
    queryFn: getChannelId,

    // WHY retry: 1 matches useChannel:
    // Consistency in failure behavior across channel-related hooks
    // Both are critical for app operation and should fail fast
    retry: 1,
  });

  return { isLoading, channelId, error: error ?? null };
}
